<app-section-grid *ngIf="!embeddedMode" [subSection]="'Edit Social Profile'"></app-section-grid>

<div class="relative w-full max-w-4xl mx-auto p-10 bg-white rounded-3xl mb-5"
     [class.border]="hasBorder"
     [class.border-gray-200]="hasBorder">

  <form [formGroup]="siteForm" (ngSubmit)="onSubmit()">

    <!-- Header -->
    <div class="text-center mb-4">
      <h1 class="text-3xl font-bold text-gray-800 tracking-tight">
        Edit Social Profile Details
      </h1>
      <p class="mt-2 text-gray-500">
        Update the social profile details below.
      </p>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-6">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-3 text-gray-600">Loading site data...</p>
    </div>

    <!-- Error Message -->
    <div *ngIf="!isLoading && errorMessage"
         class="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd" />
        </svg>
        <span class="text-red-700 text-sm">{{ errorMessage }}</span>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="!isLoading && successMessage"
         class="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd" />
        </svg>
        <span class="text-green-700 text-sm">{{ successMessage }}</span>
      </div>
    </div>

    <!-- Fields -->
    <div *ngIf="!isLoading" class="grid grid-cols-12 gap-6">

      <!-- Domain -->
      <div class="col-span-12 lg:col-span-12">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          🌐 Domain
        </label>
        <textarea
          formControlName="domainName"
          rows="1"
          placeholder="Enter domain..."
          class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
        </textarea>
      </div>

      <!-- Site URL -->
      <div class="col-span-12 lg:col-span-12">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          🔗 Social Page/Site
        </label>
        <textarea
          formControlName="siteUrl"
          rows="1"
          placeholder="Enter URL..."
          class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
        </textarea>
      </div>

      <!-- CMS Type -->
      <div class="col-span-12 lg:col-span-12">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          🧱 CMS Type
        </label>
        <textarea
          formControlName="cmsType"
          rows="1"
          placeholder="Enter CMS type..."
          class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
        </textarea>
      </div>

      <!-- Full Logo -->
      <div class="col-span-12 lg:col-span-6">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          🖼 Primary Logo
        </label>

        <div class="relative">
          <input
            type="file"
            accept="image/png"
            (change)="onFileChange($event, 'full')"
            class="absolute inset-0 opacity-0 cursor-pointer z-10" />

          <div class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50
                      text-gray-400 text-sm shadow-sm">
            {{ fullLogoFile?.name || 'Choose new file' }}
          </div>
        </div>

        <p *ngIf="fullLogoError" class="mt-1 text-xs text-red-500">
          {{ fullLogoError }}
        </p>

        <!-- Current logo preview -->
        <div *ngIf="fullLogoPreview" class="mt-3">
          <div
            class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm inline-flex items-center justify-center min-w-[200px] h-24">
            <img
              [src]="fullLogoPreview"
              class="max-h-full max-w-full object-contain"
              alt="Primary logo preview" />
          </div>
        </div>
      </div>

      <!-- Thumbnail Logo -->
      <div class="col-span-12 lg:col-span-6">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          🧩 Thumbnail Logo
        </label>

        <div class="relative">
          <input
            type="file"
            accept="image/png"
            (change)="onFileChange($event, 'thumb')"
            class="absolute inset-0 opacity-0 cursor-pointer z-10" />

          <div class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50
                      text-gray-400 text-sm shadow-sm">
            {{ thumbLogoFile?.name || 'Choose new file' }}
          </div>
        </div>

        <p *ngIf="thumbLogoError" class="mt-1 text-xs text-red-500">
          {{ thumbLogoError }}
        </p>

        <!-- Current thumbnail preview -->
        <div *ngIf="thumbPreview" class="mt-3">
          <img
            [src]="thumbPreview"
            class="w-full max-h-[75px] max-w-[75px] object-cover rounded-xl border border-gray-200 shadow-sm"
            alt="Thumbnail logo preview" />
        </div>
      </div>

    </div>

    <!-- Action Buttons (same style as user form) -->
    <div class="mt-10 pt-6 border-t border-gray-200 flex justify-end space-x-4">
      <!-- Cancel Button -->
      <button
        type="button"
        (click)="onCancel()"
        [disabled]="isSubmitting"
        class="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 
               hover:bg-gray-50 hover:border-gray-400 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
        Cancel
      </button>

      <!-- Submit Button -->
      <button
        type="submit"
        [disabled]="siteForm.invalid || isSubmitting"
        class="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold 
               hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">

        <!-- Loading Spinner -->
        <span *ngIf="isSubmitting" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
               xmlns="http://www.w3.org/2000/svg"
               fill="none"
               viewBox="0 0 24 24">
            <circle class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4">
            </circle>
            <path class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          Saving...
        </span>

        <!-- Normal State -->
        <span *ngIf="!isSubmitting">
          Save Changes
        </span>
      </button>

    </div>

  </form>
</div>

<app-footer-grid *ngIf="!embeddedMode"></app-footer-grid>
